// src/pages/Insurance.js
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert,
  Snackbar,
  Grid,
} from "@mui/material";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { postData } from "../services/request";

const SUCCESS_URL = "https://ajoo-server1.herokuapp.com/insurance/success";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#8e62a4" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#52057b",
      borderWidth: 2,
    },
  },
};

const Insurance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  // NIN from query or localStorage
  const ninFromStorage = (() => {
    try {
      const raw = localStorage.getItem("nin");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.nin || parsed?.data?.nin || "";
    } catch {
      return "";
    }
  })();
  const nin = query.get("nin") || ninFromStorage;

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("deviceId");
    localStorage.removeItem("notifications");
    localStorage.removeItem("nin");
    navigate("/", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const form = event.target;
    const formData = new FormData(form);
    const body = {};

    formData.forEach((value, key) => {
      if (value === "") return;
      if (key === "consent") {
        body[key] = true;
      } else {
        body[key] = value;
      }
    });

    try {
      // ✅ postData use karein
      const response = await postData("/api/user/insurance", body);

      if (response?.status === true) {
        // ✅ 1. Snackbar dikhayein
        setSnackbar({
          open: true,
          message: "Declaration submitted successfully!",
          severity: "success",
        });

        // ✅ 2. localStorage mein isInsured = 1 update karein
        const storedUserData = JSON.parse(
          localStorage.getItem("userData") || "{}",
        );
        storedUserData.isInsured = 1;
        localStorage.setItem("userData", JSON.stringify(storedUserData));

        // ✅ 3. Redirect karein
        setTimeout(() => navigate("/create-group"), 1500);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Submission failed.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error?.message || "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f6f7fb" }}>
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={handleLogout}
        user={{ name: "John Doe", role: "Member" }}
      />

      <Box sx={{ display: "flex" }}>
        <Sidebar
          activePath="/insurance"
          onNavigate={goTo}
          mobileOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            ml: { xs: 0, md: "180px" },
            p: { xs: 2, sm: 3, md: 5 },
          }}
        >
          <Paper
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: 900,
              mx: "auto",
              p: { xs: 3, sm: 5 },
              border: "1px solid #e9ebf0",
              borderRadius: "20px",
              boxShadow: "0 12px 35px rgba(28, 32, 50, 0.06)",
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{ color: "#52057b", fontWeight: 800, mb: 1 }}
              >
                🏥 Declaration of Health
              </Typography>
              <Typography sx={{ color: "#7a8394", fontSize: 14 }}>
                Please fill in your health information accurately.
              </Typography>
            </Box>

            {/* Hidden NIN */}
            <input type="hidden" name="nin" value={nin} />

            {/* 1. Personal Information */}
            <Typography
              sx={{
                color: "#52057b",
                fontWeight: 800,
                fontSize: 16,
                mb: 2,
                borderLeft: "4px solid #52057b",
                pl: 1.5,
              }}
            >
              Personal Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* 2. Medical Information */}
            <Typography
              sx={{
                color: "#52057b",
                fontWeight: 800,
                fontSize: 16,
                mb: 2,
                borderLeft: "4px solid #52057b",
                pl: 1.5,
              }}
            >
              Medical Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  label="Height (m)"
                  name="height"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                  name="weight"
                  type="number"
                  inputProps={{ step: "0.1" }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  label="Blood Pressure"
                  name="bloodPressure"
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            {/* 3. Health Questions */}
            <Typography
              sx={{
                color: "#52057b",
                fontWeight: 800,
                fontSize: 16,
                mb: 2,
                borderLeft: "4px solid #52057b",
                pl: 1.5,
              }}
            >
              Health Questions
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                1. Any illness, treatment, or doctor visit in the last 12
                months?
              </Typography>
              <RadioGroup row name="illness_last12_months">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                2. Do you currently have or take medication for a long-term
                condition?
              </Typography>
              <RadioGroup row name="chronic_condition_medication">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                3. Any tests, procedures, or therapy in the last 12 months?
              </Typography>
              <RadioGroup row name="tests_or_therapy_last12_months">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                4. Ever diagnosed with HIV/AIDS, Hepatitis B, or STI?
              </Typography>
              <RadioGroup row name="infectious_disease_hiv_hep_sti">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                5. Do you smoke or use tobacco?
              </Typography>
              <RadioGroup row name="tobacco_use">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                6. Do you drink alcohol?
              </Typography>
              <RadioGroup row name="alcohol_use">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                7. Any close family history of serious illness?
              </Typography>
              <RadioGroup row name="family_history_serious_illness">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                8. Do you take part in hazardous activities?
              </Typography>
              <RadioGroup row name="hazardous_activities">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                9. Have you ever been declined insurance or offered special
                terms?
              </Typography>
              <RadioGroup row name="declined_insurance">
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            {/* 4. Declaration & Consent */}
            <Typography
              sx={{
                color: "#52057b",
                fontWeight: 800,
                fontSize: 16,
                mb: 2,
                borderLeft: "4px solid #52057b",
                pl: 1.5,
              }}
            >
              Declaration & Consent
            </Typography>

            <FormControlLabel
              control={<Checkbox name="consent" value="true" required />}
              label="I declare that the information provided is true and complete."
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Signature (Type Full Name)"
                  name="signature"
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Date"
                  name="declarationDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#52057b",
                  color: "#fff",
                  py: 1.5,
                  borderRadius: "12px",
                  fontWeight: 750,
                  textTransform: "none",
                  boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
                  "&:hover": {
                    bgcolor: "#3e055e",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Submit Declaration"
                )}
              </Button>
            </Box>

            {snackbar.open && (
              <Alert
                severity={snackbar.severity}
                sx={{ mt: 3, borderRadius: "12px" }}
              >
                {snackbar.message}
              </Alert>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Insurance;
